import { find } from "lodash";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";
import useSWRImmutable from "swr/immutable";
import { AdministrationLevelEnum } from "../../features/users/constants";
import { FilteredRole, UserRole } from "../../features/users/models/role.model";
import { getTranslationForLocale } from "../../features/users/utils/options";
import fetcher from "./fetcher";

export const useRoles = () => {
    const router = useRouter();

    const {
        data = [],
        error,
        isLoading,
    } = useSWRImmutable(
        ["/api/permission-management/v1/roles", router.locale],
        ([url]) => fetcher(url)
    );

    const filterRoles = useCallback(
        (administrationLevelsToExclude: AdministrationLevelEnum[] = []) =>
            data.reduce((filteredRoles: FilteredRole[], role: UserRole) => {
                if (
                    !administrationLevelsToExclude.includes(
                        role.administrationLevel
                    )
                ) {
                    const translationForLocale = getTranslationForLocale({
                        translations: role.translations || [],
                        locale: router.locale,
                    });
                    const translatedRoleName =
                        translationForLocale?.value?.name || role?.name;

                    filteredRoles.push({
                        id: role.id,
                        name: translatedRoleName,
                        administrationLevel: role.administrationLevel,
                    });
                }
                return filteredRoles;
            }, []),
        [data, router.locale]
    );

    const companyLevelRoles = useMemo(
        () => filterRoles([AdministrationLevelEnum.GLOBAL]),
        [filterRoles]
    );

    const locationLevelRoles = useMemo(
        () =>
            filterRoles([
                AdministrationLevelEnum.GLOBAL,
                AdministrationLevelEnum.COMPANY,
            ]),
        [filterRoles]
    );

    const getLocalizedUserRole = useCallback(
        (roleId?: string) => {
            const userRole = find(data, { id: roleId });
            const translation = find(userRole?.translations, [
                "languageCode",
                router.locale,
            ]);
            return translation?.value?.name ?? userRole?.name;
        },
        [data, router.locale]
    );

    return {
        roles: data,
        companyLevelRoles,
        locationLevelRoles,
        isLoading,
        isError: error,
        getLocalizedUserRole,
    };
};
